import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts'
import { 
  BarChart3, TrendingUp, Clock, Eye, Heart, MessageCircle,
  Calendar, Lightbulb, Filter, Star
} from 'lucide-react'
import type { InstagramAnalyticsInsights, ContentDistribution } from '@/types'

import ProfileHeader from './ProfileHeader'
import KeyMetricsGrid from './KeyMetricsGrid'
import CoreThemesList from './CoreThemesList'
import AudienceTargetingBlock from './AudienceTargetingBlock'

function formatEngagementRate(rate: number): string {
  return `${(rate * 100).toFixed(3)}%`
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function ContentStrategySection({ captionsInsights }: { captionsInsights?: any }) {
  if (!captionsInsights?.account_insights) return null
  const insights = captionsInsights.account_insights
  return (
    <div>
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-violet-400/10 to-transparent dark:from-violet-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Lightbulb className="h-6 w-6 text-violet-600 dark:text-violet-400" />
            Content Strategy Insights
          </CardTitle>
          <CardDescription>AI-powered analysis of content approach and strategic positioning</CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {insights.strategy_summary && (
            <div className="relative p-6 rounded-lg bg-gradient-to-r from-violet-50 to-blue-50 dark:from-violet-950/20 dark:to-blue-950/20 border">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Star className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground mb-2">Strategic Overview</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{insights.strategy_summary}</p>
                </div>
              </div>
            </div>
          )}
          <div className="grid gap-6 md:grid-cols-2">
            <CoreThemesList themes={insights.core_themes} limit={2} showHeader={true} />
            <AudienceTargetingBlock 
              primary={insights?.audience_targeting?.primary}
              secondary={insights?.audience_targeting?.secondary}
              showHeader={true}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Content Tactics removed in current UI revision

function StrategicInsightsSection({ captionsInsights }: { captionsInsights?: any }) {
  if (!captionsInsights?.account_insights) return null
  const insights = captionsInsights.account_insights
  const hasStrategicTakeaways = insights.strategic_takeaways && insights.strategic_takeaways.length > 0
  const hasFunnel = insights.funnel_positioning && (insights.funnel_positioning.top_funnel || insights.funnel_positioning.mid_funnel)
  if (!hasStrategicTakeaways && !hasFunnel) return null
  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-6 w-6 text-violet-600 dark:text-violet-400" />
            Strategic Insights & Recommendations
          </CardTitle>
          <CardDescription>Key strategic takeaways and actionable recommendations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            {hasStrategicTakeaways && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <h4 className="font-semibold text-foreground">Strategic Takeaways</h4>
                </div>
                <div className="space-y-3">
                  {insights.strategic_takeaways.map((takeaway: string, index: number) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500 dark:bg-orange-400 mt-2 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground leading-relaxed">{takeaway}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasFunnel && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <h4 className="font-semibold text-foreground">Funnel Positioning</h4>
                </div>
                <div className="space-y-3">
                  {insights.funnel_positioning.top_funnel && (
                    <div className="p-4 rounded-lg bg-muted/50 border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">Top Funnel</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{insights.funnel_positioning.top_funnel}</p>
                    </div>
                  )}
                  {insights.funnel_positioning.mid_funnel && (
                    <div className="p-4 rounded-lg bg-muted/30 border">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">Mid Funnel</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{insights.funnel_positioning.mid_funnel}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Removed Content Style and moved Funnel Positioning into Strategic Insights section

export function InstagramAnalyticsDashboard({ data }: { data: InstagramAnalyticsInsights }) {
  const { profile_overview, posting_activity, content_distribution, engagement_metrics, temporal_trends } = data
  return (
    <div className="space-y-6">
      <ProfileHeader 
        username={profile_overview.username}
        fullName={profile_overview.full_name}
        bio={profile_overview.bio}
        isVerified={profile_overview.is_verified}
        isPrivate={profile_overview.is_private}
      />
      <ContentStrategySection captionsInsights={(data as any).captions_insights} />
      <KeyMetricsGrid 
        followers={profile_overview.followers}
        totalPostsAnalyzed={profile_overview.total_posts_analyzed}
        postsPerDay={posting_activity.posts_per_day}
        engagementRate={formatEngagementRate(engagement_metrics.avg_engagement_rate)}
        spanDays={posting_activity.span_days}
      />
      {/* ContentStyle removed; Funnel Positioning shown in Strategic Insights */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Content Distribution
            </CardTitle>
            <CardDescription>Visual breakdown of content types</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {content_distribution.map((item: ContentDistribution, index: number) => (
                <div key={`${item.type}-${index}`} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${['bg-blue-500','bg-green-500','bg-amber-500','bg-red-500'][index] || 'bg-gray-500'}`} />
                      <span className="font-medium capitalize">{item.type.replace('Graph', '').replace('Sidecar', 'Carousel')}</span>
                    </div>
                    <Badge variant="secondary">{item.count} posts</Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-muted rounded">
                      <div className="h-3 rounded" style={{ width: `${item.percentage}%`, backgroundColor: ['#3b82f6','#10b981','#f59e0b','#ef4444'][index] || '#6b7280' }} />
                    </div>
                    <div className="text-right text-sm font-semibold text-muted-foreground">{item.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5" />
              Engagement Overview
            </CardTitle>
            <CardDescription>Visual engagement performance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="relative p-4 rounded-lg bg-gradient-to-br from-pink-50 to-red-50 border-l-4 border-pink-500">
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="h-4 w-4 text-pink-500" />
                  <p className="text-sm font-medium text-pink-700">Total Likes</p>
                </div>
                <p className="text-2xl font-bold text-pink-900">{engagement_metrics.total_likes.toLocaleString()}</p>
              </div>
              <div className="relative p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-blue-500">
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle className="h-4 w-4 text-blue-500" />
                  <p className="text-sm font-medium text-blue-700">Total Comments</p>
                </div>
                <p className="text-2xl font-bold text-blue-900">{engagement_metrics.total_comments.toLocaleString()}</p>
              </div>
            </div>
            <Separator />
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Like Rate</span>
                  <Badge variant="outline">{formatEngagementRate(engagement_metrics.avg_like_rate)}</Badge>
                </div>
                <div className="h-2 w-full bg-muted rounded">
                  <div className="h-2 rounded" style={{ width: `${Math.min(engagement_metrics.avg_like_rate * 100, 100)}%`, backgroundColor: '#ec4899' }} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Comment Rate</span>
                  <Badge variant="outline">{formatEngagementRate(engagement_metrics.avg_comment_rate)}</Badge>
                </div>
                <div className="h-2 w-full bg-muted rounded">
                  <div className="h-2 rounded" style={{ width: `${Math.min(engagement_metrics.avg_comment_rate * 100, 100)}%`, backgroundColor: '#3b82f6' }} />
                </div>
              </div>
              {engagement_metrics.avg_view_rate > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">View Rate</span>
                    <Badge variant="outline">{formatEngagementRate(engagement_metrics.avg_view_rate)}</Badge>
                  </div>
                  <div className="h-2 w-full bg-muted rounded">
                    <div className="h-2 rounded" style={{ width: `${Math.min(engagement_metrics.avg_view_rate * 100, 100)}%`, backgroundColor: '#10b981' }} />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Posting Activity
            </CardTitle>
            <CardDescription>Visual timeline and frequency insights</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="relative p-4 rounded-lg bg-gradient-to-r from-green-50 to-blue-50 border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Analysis Period</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{posting_activity.span_days} days</Badge>
                  <Badge variant="secondary" className="text-xs">{posting_activity.posts_per_day} per day</Badge>
                </div>
              </div>
              <div className="text-xs text-green-600 font-medium">
                {formatDate(posting_activity.first_post_date)} → {formatDate(posting_activity.last_post_date)}
              </div>
            </div>
            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-3">Weekly Posting Distribution</h4>
              {(() => {
                const sortedDays = Object.entries(temporal_trends.daily_distribution).sort(([a], [b]) => {
                  const dayOrder = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
                  return dayOrder.indexOf(a) - dayOrder.indexOf(b)
                })
                const chartData = sortedDays.map(([day, count]) => ({ day, posts: count as number }))
                const chartConfig = { posts: { label: 'Posts' } } satisfies ChartConfig
                return (
                  <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[220px] w-full">
                    <RadarChart data={chartData} margin={{ top: 10, right: 16, bottom: 10, left: 16 }}>
                      <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                      <PolarGrid />
                      <PolarAngleAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <PolarRadiusAxis tick={false} />
                      <Radar dataKey="posts" stroke="hsl(var(--foreground))" fill="hsl(var(--foreground))" fillOpacity={0.1} strokeWidth={2} />
                    </RadarChart>
                  </ChartContainer>
                )
              })()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Performing Posts
            </CardTitle>
            <CardDescription>Your highest engagement content</CardDescription>
          </CardHeader>
          <CardContent>
            {engagement_metrics.standout_posts.length > 0 ? (
              <div className="space-y-3 max-h-[360px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                {engagement_metrics.standout_posts
                  .sort((a, b) => b.engagement_rate - a.engagement_rate)
                  .map((post) => {
                    const caption = post.caption || 'No caption available'
                    const truncatedCaption = caption.length > 100 ? `${caption.substring(0, 100)}...` : caption
                    const progressPercentage = post.engagement_rate * 100
                    return (
                      <a key={post.post_id} href={`https://instagram.com/p/${post.shortcode}`} target="_blank" rel="noopener noreferrer" className="block p-4 rounded-lg border hover:shadow-md transition-all duration-200 cursor-pointer group">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground">{formatDate(new Date(post.timestamp * 1000).toISOString())}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">View Post</span>
                          </div>
                        </div>
                        <p className="mb-3 text-sm text-muted-foreground line-clamp-2">{truncatedCaption}</p>
                        <div className="flex items-center justify-end">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-muted rounded-full h-2">
                              <div className="h-2 bg-green-500 rounded-full" style={{ width: `${Math.min(progressPercentage, 100)}%` }} />
                            </div>
                            <span className="font-bold text-green-700 text-sm">{formatEngagementRate(post.engagement_rate)}</span>
                          </div>
                        </div>
                      </a>
                    )
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <TrendingUp className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No standout posts identified</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <StrategicInsightsSection captionsInsights={(data as any).captions_insights} />

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Data processed on {formatDate(data.processing_timestamp)}</span>
            <Badge variant="outline">Analytics Ready</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default InstagramAnalyticsDashboard


